import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileText, Film, Image as ImageIcon, Trash2, Upload } from "lucide-react";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";

const KIND_ICONS = { image: ImageIcon, video: Film, document: FileText };
const KIND_FILTERS = [
  { key: "", label: "Tous" },
  { key: "image", label: "Photos" },
  { key: "video", label: "Vidéos" },
  { key: "document", label: "Documents" },
];

const fmtSize = (bytes) =>
  bytes > 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} Mo` : `${Math.round(bytes / 1024)} Ko`;

export default function MediaManager() {
  const [items, setItems] = useState([]);
  const [kind, setKind] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () =>
    api.get(`/admin/media${kind ? `?kind=${kind}` : ""}`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(files.length > 1 ? `${files.length} fichiers ajoutés.` : "Fichier ajouté.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const toggleGallery = async (media, value) => {
    await api.patch(`/admin/media/${media.id}`, { in_gallery: value });
    toast.success(value ? "Vidéo ajoutée à la galerie publique." : "Vidéo retirée de la galerie.");
    load();
  };

  const remove = async (id) => {
    await api.delete(`/admin/media/${id}`);
    toast.success("Média supprimé.");
    load();
  };

  return (
    <div data-testid="media-manager">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-ivory">Médiathèque — {items.length} fichier(s)</h2>
          <p className="mt-1 text-xs text-ivory/40">
            Photos, vidéos et documents stockés de façon pérenne. Activez « Galerie » sur une vidéo
            pour l'afficher sur la page Galerie du site.
          </p>
        </div>
        <label className="btn-primary !h-10 cursor-pointer !px-5 !text-[11px]" data-testid="media-upload-button">
          <Upload className="h-4 w-4" strokeWidth={1.5} />
          {uploading ? "Envoi…" : "Ajouter des fichiers"}
          <input ref={fileRef} type="file" multiple accept="image/*,video/mp4,video/webm,video/quicktime,.pdf,.doc,.docx,.xls,.xlsx" onChange={upload} className="hidden" data-testid="media-upload-input" />
        </label>
      </div>

      <div className="mb-6 flex flex-wrap gap-2" data-testid="media-filters">
        {KIND_FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setKind(f.key)}
            data-testid={`media-filter-${f.key || "tous"}`}
            className={`rounded-md border px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.15em] transition-colors ${
              kind === f.key
                ? "border-copper bg-copper text-ivory"
                : "border-white/10 text-ivory/60 hover:border-copper/50 hover:text-ivory"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-ivory/40" data-testid="media-empty">
          Aucun fichier pour le moment. Ajoutez vos photos, vidéos et documents.
        </p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="media-grid">
          {items.map((media) => {
            const Icon = KIND_ICONS[media.kind] || FileText;
            const fileUrl = `${process.env.REACT_APP_BACKEND_URL}/api/media/${media.id}/file`;
            return (
              <div key={media.id} className="overflow-hidden rounded-lg border border-white/10 bg-coal/60" data-testid={`media-item-${media.id}`}>
                {media.kind === "image" ? (
                  <img src={fileUrl} alt={media.filename} className="h-32 w-full object-cover" loading="lazy" />
                ) : media.kind === "video" ? (
                  <video src={fileUrl} className="h-32 w-full object-cover" muted preload="metadata" />
                ) : (
                  <div className="flex h-32 items-center justify-center bg-white/5">
                    <Icon className="h-8 w-8 text-ivory/30" strokeWidth={1.25} />
                  </div>
                )}
                <div className="p-3">
                  <p className="truncate text-xs font-semibold text-ivory" title={media.filename}>
                    {media.filename}
                  </p>
                  <p className="mt-0.5 text-[10px] uppercase tracking-wider text-ivory/35">
                    {media.kind} · {fmtSize(media.size)}
                  </p>
                  <div className="mt-2.5 flex items-center justify-between gap-2">
                    {media.kind === "video" ? (
                      <label className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-ivory/50">
                        Galerie
                        <Switch checked={!!media.in_gallery} onCheckedChange={(v) => toggleGallery(media, v)} data-testid={`media-gallery-${media.id}`} />
                      </label>
                    ) : (
                      <span />
                    )}
                    <button onClick={() => remove(media.id)} aria-label="Supprimer" className="flex h-7 w-7 items-center justify-center rounded-md border border-white/10 text-ivory/40 transition-colors hover:border-red-400/50 hover:text-red-400" data-testid={`media-delete-${media.id}`}>
                      <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
